import { Transaction } from '../types';
import { FYCgtResult } from './cgtCalculator';
import { formatDate } from './formatters';

/**
 * Client-side CSV builders + download helper for the Tax tab exports.
 *
 * IMPORTANT: this module does NOT recompute or convert any CGT figure. It prints
 * what the engine produced (`FYCgtResult` and its `disposalsInFY`), which is
 * already in AUD — converted per leg at that leg's own date — and already rounded
 * to the cent. Re-deriving a column here (halving a gain, applying the marginal
 * rate) would reintroduce exactly the screen-vs-export disagreement that rounding
 * once in the engine exists to prevent.
 *
 * Money is emitted as plain AUD numbers (2dp, no symbol/separators) so the columns
 * stay numeric and sortable in Excel / Google Sheets — the "(AUD)" header denotes
 * the currency. Dates reuse the app's `formatDate`.
 */

export const DISCLAIMER =
  'This report is a summary of your manually recorded transactions, calculated using the FIFO method and simplified ATO CGT rules. Figures are estimates only. ' +
  'AUD values are converted at the exchange rate for each transaction’s own date, using Reserve Bank of Australia daily rates and falling back to European Central Bank rates. A date falling on a weekend or public holiday uses the previous business day. ' +
  'Amounts are rounded to the nearest cent per transaction. ' +
  'This is not tax advice - confirm with your registered tax agent before lodging.';

type Cell = string | number;

/** Quote a field iff it contains a comma, quote, or newline; double inner quotes. */
function escapeCsv(value: Cell): string {
  const s = value == null ? '' : String(value);
  if (/[",\r\n]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

/** Join rows into RFC-4180 CSV text (CRLF line endings, which Excel prefers). */
function rowsToCsv(rows: Cell[][]): string {
  return rows.map((row) => row.map(escapeCsv).join(',')).join('\r\n');
}

/** Plain AUD amount, 2dp. */
function money(n: number): string {
  return isFinite(n) ? n.toFixed(2) : '0.00';
}

/** A clean decimal string (trims float artifacts, caps at 8dp, no exponent noise). */
export function cleanNum(n: number): string {
  return isFinite(n) ? String(parseFloat(n.toFixed(8))) : '0';
}

/** Marginal rate as a percent string (e.g. "32.5%"), matching the UI. */
export function ratePct(rate: number): string {
  const pct = rate * 100;
  return `${pct.toFixed(pct % 1 === 0 ? 0 : 1)}%`;
}

/** Hold period in whole months, matching the breakdown's display. */
export function holdPeriod(days: number): string {
  const m = Math.round(days / 30.436875);
  return m < 1 ? '<1 month' : `${m} month${m === 1 ? '' : 's'}`;
}

/** "FY 2025-26" style label for the given AU financial year. */
export function fyShortLabel(fy: { startYear: number; endYear: number }): string {
  return `FY ${fy.startYear}-${String(fy.endYear).slice(2)}`;
}

/** Filename for the CGT report, e.g. MyCryptoPlanner_CGT_Report_FY2025-26.csv */
export function cgtReportFilename(fy: { startYear: number; endYear: number }): string {
  return `MyCryptoPlanner_CGT_Report_FY${fy.startYear}-${String(fy.endYear).slice(2)}.csv`;
}

/** Filename for the transactions export, e.g. MyCryptoPlanner_Transactions_FY2025-26.csv */
export function transactionsFilename(fy: { startYear: number; endYear: number }): string {
  return `MyCryptoPlanner_Transactions_FY${fy.startYear}-${String(fy.endYear).slice(2)}.csv`;
}

const DISPOSAL_HEADERS: string[] = [
  'Coin',
  'Symbol',
  'Quantity sold',
  'Acquisition date',
  'Disposal date',
  'Hold period',
  'Acquisition fee (AUD)',
  'Proceeds (AUD)',
  'Disposal fee (AUD)',
  'Cost basis (AUD)',
  'Gross gain/loss (AUD)',
  'Discount eligible',
  'Discount amount (AUD)',
  'Taxable gain (AUD)',
  'CGT owed (AUD)',
];

export interface CgtReportParams {
  result: FYCgtResult;
  fy: { startYear: number; endYear: number };
  marginalRate: number;
  exportDate: Date;
}

/** Build the headline CGT report CSV (disclaimer + summary + per-disposal table). */
export function buildCgtReportCsv({ result, fy, marginalRate, exportDate }: CgtReportParams): string {
  const rows: Cell[][] = [];

  // ── Metadata / disclaimer block ──
  rows.push(['MyCryptoPlanner: Realised CGT Report']);
  rows.push(['Financial year', fyShortLabel(fy)]);
  rows.push(['Export date', formatDate(exportDate.toISOString())]);
  rows.push(['Disclaimer', DISCLAIMER]);

  // ── Summary block ──
  rows.push([]);
  rows.push(['Summary']);
  rows.push(['Net capital gain (AUD)', money(result.netCapitalGain)]);
  rows.push(['CGT discount applied (AUD)', money(result.discountApplied)]);
  rows.push(['Taxable gain (AUD)', money(result.taxableGain)]);
  rows.push(['Marginal rate used', ratePct(marginalRate)]);
  rows.push(['Est. CGT owed (AUD)', money(result.estCGT)]);

  // ── Disposals table ──
  rows.push([]);
  rows.push([
    "Note: Per-disposal 'CGT owed' figures are shown individually and will not add up to the total Est. CGT owed above. The summary applies capital losses against gains before the 50% discount (ATO order of operations); the per-disposal rows do not.",
  ]);
  rows.push(DISPOSAL_HEADERS);

  if (result.disposalsInFY.length === 0) {
    rows.push(['No realised disposals this financial year.']);
    return rowsToCsv(rows);
  }

  // Newest sell first, then earliest parcel first (mirrors the on-screen order).
  const ordered = [...result.disposalsInFY].sort((a, b) => {
    if (a.disposalTs !== b.disposalTs) return b.disposalTs - a.disposalTs;
    return new Date(a.acquisitionDate).getTime() - new Date(b.acquisitionDate).getTime();
  });

  for (const d of ordered) {
    rows.push([
      d.name,
      d.symbol,
      cleanNum(d.quantity),
      formatDate(d.acquisitionDate),
      formatDate(d.disposalDate),
      holdPeriod(d.heldDays),
      money(d.acquisitionFeeAud),
      money(d.proceeds),
      money(d.disposalFeeAud),
      money(d.costBasis),
      money(d.gain),
      d.discountEligible ? 'Yes' : 'No',
      money(d.discountAmount),
      money(d.taxableGain),
      money(d.cgtOwed),
    ]);
  }

  return rowsToCsv(rows);
}

const TRANSACTION_HEADERS: string[] = [
  'Date',
  'Type',
  'Coin',
  'Symbol',
  'Quantity',
  'Price',
  'Currency',
  'Fee',
  'Fee currency',
  'Wallet',
  'Transaction ID',
];

function walletCell(tx: Transaction): string {
  if (tx.wallet) return tx.wallet;
  if (tx.fromWallet || tx.toWallet) return `${tx.fromWallet ?? ''} → ${tx.toWallet ?? ''}`.trim();
  return '';
}

export interface TransactionsCsvParams {
  transactions: Transaction[];
  exportDate: Date;
  fy?: { startYear: number; endYear: number };
}

/**
 * Build the transactions CSV, one row per supplied transaction. Callers pass the
 * set already scoped to the selected FY; `fy` (when given) only labels the report.
 */
export function buildTransactionsCsv({ transactions, exportDate, fy }: TransactionsCsvParams): string {
  const rows: Cell[][] = [];

  rows.push(['MyCryptoPlanner: Transactions Export']);
  if (fy) rows.push(['Financial year', fyShortLabel(fy)]);
  rows.push(['Export date', formatDate(exportDate.toISOString())]);
  rows.push([]);
  rows.push(TRANSACTION_HEADERS);

  // Chronological order (oldest first), tie-broken by entry timestamp.
  const ordered = [...transactions].sort((a, b) => {
    const da = new Date(a.date).getTime();
    const db = new Date(b.date).getTime();
    return da !== db ? da - db : a.timestamp - b.timestamp;
  });

  for (const tx of ordered) {
    rows.push([
      formatDate(tx.date),
      tx.type,
      tx.name,
      tx.symbol,
      cleanNum(tx.amount),
      cleanNum(tx.price),
      'USD', // prices are stored in USD (the app's base unit)
      // The fee EXACTLY as the user typed it, in its own currency — not converted.
      // A transfer's cost lives in network_fee and is reported as a USD figure.
      tx.fee != null ? cleanNum(tx.fee) : tx.networkFee != null ? cleanNum(tx.networkFee) : '',
      tx.fee != null ? (tx.feeCurrency ?? '') : tx.networkFee != null ? 'USD' : '',
      walletCell(tx),
      tx.id,
    ]);
  }

  return rowsToCsv(rows);
}

/** Trigger a browser download of CSV text (UTF-8 BOM so Excel reads it cleanly). */
export function downloadCsv(filename: string, csv: string): void {
  const blob = new Blob(['﻿', csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
